import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Provider } from "react-redux";
import store from "./store";
import { AuthProvider } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Offering from "./pages/Offering";
import B2BLandingPage from "./pages/B2BLandingPage";
import AdminPage from "./pages/AdminPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import CustomerPortalPage from "./pages/CustomerPortalPage";
import TrackingPage from "./pages/TrackingPage";
import OrderTrackingPage from "./pages/OrderTrackingPage";
import ProductPage from "./pages/ProductPage";
import ProductGridPage from "./pages/ProductGridPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrderConfirmationPage from "./pages/OrderConfirmationPage";
import OrderErrorPage from "./pages/OrderErrorPage";
import LoginPage from "./pages/LoginPage";
import SignUpPage from "./pages/SignUpPage";
import AuthPage from "./pages/AuthPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AccountPage from "./pages/AccountPage";
import NotFoundPage from "./pages/NotFoundPage";

// ── Dev-only imports — remove before launch ───────────────────────────────
import { DEV_ROUTES_ENABLED } from "./devRoutes";
import DevRouteTable from "./components/dev/DevRouteTable";
// ─────────────────────────────────────────────────────────────────────────

import "./App.css";

/* ── Scroll to top on every route change ─────────────────────────────────
   Wrapped inside BrowserRouter so useLocation() is available.
   Also handles the case where a form/dialog navigates to a new page
   and the scroll position is inherited from the previous view. ──────── */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <div className="App">
          <BrowserRouter>
            <ScrollToTop />
            {/* Anchor target for programmatic scroll — sits above everything */}
            <div id="page-top" />
            <Routes>
              {/* ── Production routes ──────────────────────────────────── */}
              {/* B2B landing */}
              <Route path="/" element={<ProductGridPage />} />

              {/* D2C funnel */}
              {/* <Route path="/offering" element={<Offering />} /> */}
              <Route path="/offering/:instanceId" element={<Offering />} />
              <Route path="/shop/:instanceId" element={<Offering />} />

              {/* Product catalogue */}
              <Route path="/shop" element={<ProductGridPage />} />

              {/* Product detail */}
              <Route path="/product/:productId" element={<ProductPage />} />

              {/* ── Cart & Checkout ────────────────────────────────────── */}
              <Route path="/cart" element={<CartPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route
                path="/order/confirmation"
                element={<OrderConfirmationPage />}
              />
              <Route path="/order/error" element={<OrderErrorPage />} />

              {/* ── Payments (legacy / buy-now success) ───────────────── */}
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
              <Route path="/payment/cancel" element={<PaymentCancelPage />} />

              {/* Post-sale */}
              <Route path="/portal/:orderId" element={<CustomerPortalPage />} />
              <Route
                path="/tracking/:trackingNumber"
                element={<TrackingPage />}
              />
              <Route path="/track" element={<OrderTrackingPage />} />
              <Route path="/track/:token" element={<OrderTrackingPage />} />

              {/* Internal */}

              {/* ── Auth & User ────────────────────────────────────────── */}
              <Route path="/login" element={<AuthPage />} />
              <Route path="/signup" element={<AuthPage />} />
              <Route path="/auth" element={<AuthPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </BrowserRouter>
          <CookieConsentBanner />
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
    </Provider>
  );
}

export default App;
